import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { pallBearerService } from '../../../../services/pallBearerInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('PallBearerInput');

export const PallBearerInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { pallBearerId?: string; isEdit?: boolean }>, string>>();
  const pallBearerId = (route.params as any)?.pallBearerId as string | undefined;
  const isEdit = !!(route.params as any)?.isEdit;

  // Phone number formatting function
  const formatPhoneNumber = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Limit to 10 digits
    const limited = cleaned.substring(0, 10);

    // Format as (xxx) xxx-xxxx
    if (limited.length >= 6) {
      return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
    } else if (limited.length >= 3) {
      return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    } else if (limited.length > 0) {
      return `(${limited}`;
    }
    return limited;
  };

  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    choice: '',
    notes: '',
  });

  const [saving, setSaving] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedPallBearerId, setSavedPallBearerId] = useState<string | undefined>();

  useEffect(() => {
    if (isEdit && pallBearerId) {
      (async () => {
        try {
          // Load existing pall bearer record
          const record = await pallBearerService.getPallBearerById(pallBearerId);
          if (record) {
            logger.debug('[Edit Debug] Loading pall bearer for edit:', record);
            setFormData({
              fullName: record.fullName || '',
              phoneNumber: record.phoneNumber ? formatPhoneNumber(record.phoneNumber) : '',
              email: record.email || '',
              choice: record.choice || '',
              notes: record.notes || '',
            });
          }
        } catch (e) {
          logger.debug('Failed to load pall bearer for edit', e);
        }
      })();
    }
  }, [isEdit, pallBearerId]);

  const choices = ['First Choice', 'Second Choice', 'Honorary'];

  // Email validation function
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handlePhoneChange = (text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({...formData, phoneNumber: formatted});
  };

  const handleSave = async () => {
    if (!formData.fullName.trim()) {
      Alert.alert('Error', 'Please enter at least the full name');
      return;
    }

    // Validate email if provided
    if (formData.email.trim() && !validateEmail(formData.email.trim())) {
      Alert.alert('Error', 'Please enter a valid email address');
      return;
    }

    try {
      setSaving(true);

      const pallBearerUpdateData = {
        fullName: formData.fullName.trim(),
        phoneNumber: formData.phoneNumber.replace(/\D/g, ''), // Strip formatting for storage
        email: formData.email.trim().toLowerCase(),
        choice: formData.choice.trim(),
        notes: formData.notes.trim(),
      };

      // Create or update pall bearer record
      let savedId = pallBearerId as string | undefined;
      if (isEdit && pallBearerId) {
        const updated = await pallBearerService.updatePallBearer(pallBearerId, pallBearerUpdateData);
        // Handle wrapped response format { success, data, message }
        const updatedData = updated?.data || updated;
        savedId = updatedData.id;
        logger.debug('Pall bearer updated successfully:', updatedData);

        // For editing, navigate directly to lock/unlock screen
        Alert.alert('Success', 'Pall bearer updated successfully!', [
          {
            text: 'OK',
            onPress: () => navigation.navigate('PallBearerLockUnlock', { pallBearerId: savedId })
          }
        ]);
      } else {
        const saved = await pallBearerService.createPallBearer(pallBearerUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
        logger.debug('Pall bearer created successfully:', createdData);

        // Store the saved ID for navigation
        setSavedPallBearerId(savedId);

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
      }
    } catch (error) {
      logger.error('Error saving pall bearer:', error);
      Alert.alert('Error', 'Failed to save pall bearer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      fullName: '',
      phoneNumber: '',
      email: '',
      choice: '',
      notes: '',
    });
    setShowAddAnotherDone(false);
    setSaving(false);
  };

  const handleDone = () => {
    setSaving(false);
    const idToUse = savedPallBearerId || pallBearerId;
    if (idToUse) {
      navigation.navigate('PallBearerLockUnlock', { pallBearerId: idToUse });
    } else {
      Alert.alert('Error', 'Missing pall bearer ID');
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          {isEdit ? 'Edit' : 'Add'} Your Pall Bearer Details
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name:</Text>
            <TextInput
              style={styles.input}
              value={formData.fullName}
              onChangeText={(text) => setFormData({...formData, fullName: text})}
              placeholder="Enter full name (e.g., John Smith)"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number:</Text>
            <TextInput
              style={styles.input}
              value={formData.phoneNumber}
              onChangeText={handlePhoneChange}
              placeholder="Enter phone number (e.g., (555) 555-1212)"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email:</Text>
            <TextInput
              style={styles.input}
              value={formData.email}
              onChangeText={(text) => setFormData({...formData, email: text})}
              placeholder="Enter email address (e.g., john@example.com)"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="email"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Choice:</Text>
            <TouchableOpacity
              style={styles.dropdownButton}
              onPress={() => setShowDropdown(!showDropdown)}
            >
              <Text style={styles.dropdownText}>
                {formData.choice || 'Select a choice'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
            {showDropdown && (
              <View style={styles.dropdownList}>
                {choices.map((choiceOption, index) => (
                  <TouchableOpacity
                    key={index}
                    style={styles.dropdownItem}
                    onPress={() => {
                      setFormData({...formData, choice: choiceOption});
                      setShowDropdown(false);
                    }}
                  >
                    <Text style={styles.dropdownItemText}>{choiceOption}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({...formData, notes: text})}
              placeholder="Enter any additional notes or special instructions"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.saveButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.saveButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              <TouchableOpacity
                style={[styles.saveButton, saving && { opacity: 0.7 }]}
                onPress={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.saveButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                )}
              </TouchableOpacity>
            </View>
          )}
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
   backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
  dropdownButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  dropdownText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#1C2C4F',
  },
  dropdownList: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    marginTop: ms(5),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  dropdownItem: {
    padding: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  dropdownItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    flex: 1,
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    flex: 1,
  },
});

export default PallBearerInputScreen;
